import { MenuNavLink } from '@/components/MainPageLink';
import TxAnchor from '@/components/TxAnchor';
import { useAdminPerms } from '@/hooks/auth';
import { serverNameAtom, txConfigStateAtom } from '@/hooks/status';
import { cn } from '@/lib/utils';
import { TxConfigState } from '@shared/enums';
import { GlobalStatusType } from '@shared/socketioTypes';
import { useAtomValue } from 'jotai';
import { ActivityIcon, BoxIcon, ChevronDownIcon, ChevronRightSquareIcon, DatabaseIcon, DnaIcon, EyeIcon, FileEditIcon, HistoryIcon, HourglassIcon, LayoutDashboardIcon, ListChecksIcon, MapIcon, PackageIcon, RadarIcon, ScanSearchIcon, ScrollTextIcon, SettingsIcon, ShieldCheckIcon, StethoscopeIcon, TerminalSquareIcon, UserRoundCogIcon, UsersIcon, ZapIcon } from 'lucide-react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { useBackendApi } from '@/hooks/fetch';
import { useLocation } from 'wouter';


//Separate component to prevent re-render of the entire menu
function ServerName() {
    return useAtomValue(serverNameAtom);
}

type PendingServerConfigureProps = {
    txConfigState?: Exclude<TxConfigState, TxConfigState.Ready>;
}

function PendingServerConfigure({ txConfigState }: PendingServerConfigureProps) {
    const [currLocation] = useLocation();
    const [linkHref, setLinkHref] = useState('');
    const linkText = useRef('');

    //This effect is done to prevent the link from popping up in the delay between ui change 
    // and the pendingStep state atom being updated from the socket.io event
    useEffect(() => {
        let newHref = '';
        if (txConfigState === TxConfigState.Setup && !currLocation.startsWith('/server/setup')) {
            newHref = '/server/setup';
            linkText.current = 'Go to the setup page!';
        } else if (txConfigState === TxConfigState.Deployer && !currLocation.startsWith('/server/deployer')) {
            newHref = '/server/deployer';
            linkText.current = 'Go to the deployer page!';
        } else {
            newHref = '';
        }

        if (!newHref) {
            setLinkHref('');
            return;
        } else {
            const timeout = setTimeout(() => {
                setLinkHref(newHref);
            }, 500);
            return () => clearTimeout(timeout);
        }
    }, [currLocation, txConfigState]);

    return (
        <div className='absolute inset-0 flex flex-col items-center justify-center gap-4'>
            <HourglassIcon className='h-12 w-12 opacity-75 animate-pulse' />
            <p className='text-center text-lg tracking-wider font-light opacity-75'>
                You need to configure your server to be able to start it.
            </p>
            {linkHref ? (
                <TxAnchor href={linkHref} className='animate-toastbar-enter'>
                    {linkText.current}
                </TxAnchor>
            ) : (
                <TxAnchor href='#' className='animate-toastbar-leave pointer-events-none'>
                    {linkText.current || <>&nbsp;</>}
                </TxAnchor>
            )}
        </div>
    )
}

export default function ServerMenu() {
    const txConfigState = useAtomValue(txConfigStateAtom);
    const { hasPerm } = useAdminPerms();

    const isConfigPending = txConfigState !== TxConfigState.Ready;
    return <div className='relative'>
        {isConfigPending && <PendingServerConfigure txConfigState={txConfigState} />}
        <div className={cn(isConfigPending && 'opacity-0 pointer-events-none')}>
            <h2 className="mb-1.5 text-lg font-semibold tracking-tight line-clamp-1">
                <ServerName />
            </h2>
            <div className="space-y-0.5 select-none">
                <MenuNavLink href="/">
                    <LayoutDashboardIcon className="mr-2 h-4 w-4" />Dashboard
                </MenuNavLink>

                <MenuSection id="players" title="Players">
                    <MenuNavLink href="/players">
                        <UsersIcon className="mr-2 h-4 w-4" />Players
                    </MenuNavLink>
                    <MenuNavLink href="/monitoring/live-map">
                        <MapIcon className="mr-2 h-4 w-4" />Live Map<NewBadge />
                    </MenuNavLink>
                    <MenuNavLink href="/history">
                        <HistoryIcon className="mr-2 h-4 w-4" />History
                    </MenuNavLink>
                    <MenuNavLink href="/allowlist">
                        <ListChecksIcon className="mr-2 h-4 w-4" />Allowlist
                    </MenuNavLink>
                </MenuSection>

                <MenuSection id="server" title="Server">
                    <MenuNavLink href="/server/console" disabled={!hasPerm('console.view')}>
                        <ChevronRightSquareIcon className="mr-2 h-4 w-4" />Live Console
                    </MenuNavLink>
                    <MenuNavLink href="/server/resources">
                        <BoxIcon className="mr-2 h-4 w-4" />Resources
                    </MenuNavLink>
                    <MenuNavLink href="/server/server-log" disabled={!hasPerm('server.log.view')}>
                        <EyeIcon className="mr-2 h-4 w-4" />Server Log
                    </MenuNavLink>
                    <MenuNavLink href="/server/cfg-editor" disabled={!hasPerm('server.cfg.editor')}>
                        <FileEditIcon className="mr-2 h-4 w-4" />CFG Editor
                    </MenuNavLink>
                    <MenuNavLink href="/system/artifacts" disabled={!hasPerm('all_permissions')}>
                        <PackageIcon className="mr-2 h-4 w-4" />Artifacts<NewBadge />
                    </MenuNavLink>
                </MenuSection>

                <MenuSection id="monitoring" title="Monitoring">
                    <MenuNavLink href="/insights/player-drops">
                        <ActivityIcon className="mr-2 h-4 w-4" />Player Drops
                    </MenuNavLink>
                    <MenuNavLink href="/monitoring/console-analyzer" disabled={!hasPerm('txadmin.log.view')}>
                        <ScanSearchIcon className="mr-2 h-4 w-4" />Console Analyzer<NewBadge /><ConsoleCriticalBadge enabled={hasPerm('txadmin.log.view')} />
                    </MenuNavLink>
                    <MenuNavLink href="/monitoring/event-monitor" disabled={!hasPerm('txadmin.log.view')}>
                        <RadarIcon className="mr-2 h-4 w-4" />Event Monitor<NewBadge />
                    </MenuNavLink>
                    <MenuNavLink href="/monitoring/state-bags" disabled={!hasPerm('txadmin.log.view')}>
                        <DatabaseIcon className="mr-2 h-4 w-4" />State Bag Monitor<NewBadge />
                    </MenuNavLink>
                </MenuSection>

                <MenuSection id="administration" title="Administration">
                    <MenuNavLink href="/admins" disabled={!hasPerm('manage.admins')}>
                        <UserRoundCogIcon className="mr-2 h-4 w-4" />Admins
                    </MenuNavLink>
                    <MenuNavLink href="/settings" disabled={!hasPerm('settings.view')}>
                        <SettingsIcon className="mr-2 h-4 w-4" />Settings
                    </MenuNavLink>
                    <MenuNavLink href="/system/action-log" disabled={!hasPerm('txadmin.log.view')}>
                        <ScrollTextIcon className="mr-2 h-4 w-4" />Action Log
                    </MenuNavLink>
                </MenuSection>

                <MenuSection id="system" title="System" defaultOpen={false}>
                    <MenuNavLink href="/system/master-actions">
                        <ZapIcon className="mr-2 h-4 w-4" />Master Actions
                    </MenuNavLink>
                    <MenuNavLink href="/system/diagnostics">
                        <StethoscopeIcon className="mr-2 h-4 w-4" />Diagnostics
                    </MenuNavLink>
                    <MenuNavLink href="/system/console-log" disabled={!hasPerm('txadmin.log.view')}>
                        <TerminalSquareIcon className="mr-2 h-4 w-4" />Console Log
                    </MenuNavLink>
                    <MenuNavLink href="/settings/ban-templates" disabled={!hasPerm('settings.write')}>
                        <ShieldCheckIcon className="mr-2 h-4 w-4" />Ban Templates
                    </MenuNavLink>
                </MenuSection>

                {window.txConsts.showAdvanced && (
                    <MenuNavLink href="/advanced" className='text-accent' disabled={!hasPerm('all_permisisons')}>
                        <DnaIcon className="mr-2 h-4 w-4" />Advanced
                    </MenuNavLink>
                )}
                {import.meta.env.DEV && (
                    <MenuNavLink href="/test" className='text-accent'>
                        <DnaIcon className="mr-2 h-4 w-4" />Test
                    </MenuNavLink>
                )}
            </div>
        </div>
    </div>
}

/**
 * Vanta : menu latéral par sections repliables (façon Sensity), état mémorisé par section.
 */
function MenuSection({ id, title, children, defaultOpen = true }: { id: string; title: string; children: ReactNode; defaultOpen?: boolean }) {
    const storageKey = `vantaMenu:${id}`;
    const [open, setOpen] = useState<boolean>(() => {
        try {
            const saved = window.localStorage.getItem(storageKey);
            return saved === null ? defaultOpen : saved === '1';
        } catch { return defaultOpen; }
    });
    const toggle = () => {
        setOpen((prev) => {
            try { window.localStorage.setItem(storageKey, prev ? '0' : '1'); } catch { }
            return !prev;
        });
    };
    return (
        <div className="pt-3">
            <button
                type="button"
                onClick={toggle}
                className="w-full flex items-center justify-between px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
                {title}
                <ChevronDownIcon className={cn('h-3.5 w-3.5 transition-transform', !open && '-rotate-90')} />
            </button>
            {open && <div className="space-y-0.5">{children}</div>}
        </div>
    );
}

function NewBadge() {
    return <span className="ml-auto rounded bg-destructive px-1.5 py-px text-[10px] font-bold uppercase leading-4 text-destructive-foreground">new</span>;
}

/**
 * Nombre de findings critiques du boot courant (Console Analyzer), rafraîchi chaque minute.
 */
function ConsoleCriticalBadge({ enabled }: { enabled: boolean }) {
    const summaryApi = useBackendApi<any>({ method: 'GET', path: '/vanta/console/summary' });
    const { data } = useSWR(enabled ? '/vanta/console/summary#menu' : null, async () => {
        const resp = await summaryApi({});
        return resp && !('error' in resp) ? resp : null;
    }, { refreshInterval: 60_000, revalidateOnFocus: false });
    const critical = Number(data?.boot?.counts?.critical ?? 0);
    if (!critical) return null;
    return <span className="ml-1.5 rounded bg-warning/80 px-1.5 py-px text-[10px] font-bold leading-4 text-black">{critical}</span>;
}
